import React, { useEffect, useState, useMemo } from "react";
import type { TriggerKey, RefreshTriggers } from "../../models/ArtworkDataTriggers";
import { ArtworkReference, ArtworkReferenceType } from "../../models/ArtworkReference";
import ArtworkReferencePanel from "./ArtworkReferencePanel";
import { Box, Paper, Typography, Tooltip, Divider, List, ListItem, ListItemText, Chip } from '@mui/material';
import './ArtReferenceStyleSheet.css';

//Support Safe Api Calls
import { useUser } from '../../components/UserContext';
import { createApiClient, useApiClient } from "../../components/useApiClient";
import { useSafeRequest } from "../../components/useSafeRequest";

interface Props {
    artworkId: number;
    type: ArtworkReferenceType; // "Literature" or "Exhibit"
    refresh: RefreshTriggers;
}

const ArtworkReferenceManager: React.FC<Props> = ({ artworkId, type, refresh }) => {

    const [refs, setRefs] = useState<ArtworkReference[]>([]);
    const [search, setSearch] = useState("");
    const [selected, setSelected] = useState<ArtworkReference | null>(null);
    const [editMode, setEditMode] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [isOpen, setIsOpen] = useState(false);

    // Load references
    const { activeClientId, getAccessToken, isAdmin, isOwner, isValuer, activeClientLicense } = useUser();
    const isViewOnly = !activeClientLicense || activeClientLicense?.licenseExpired || !activeClientLicense?.canAddArtworks;
    const canEditArtwork = (isAdmin || isOwner || isValuer) && !isViewOnly;
    const clientIdRef = React.useRef<number | null>(null);
    React.useEffect(() => { clientIdRef.current = activeClientId; }, [activeClientId]);

    // *** Setup safe API Calls ***
    const { put, post, delete: del } = useApiClient();

    const api = React.useMemo(
        () =>
            createApiClient(
                { getAccessToken, getClientId: () => clientIdRef.current },
                { timeoutMs: 12000 }
            ),
        [getAccessToken]
    );

    const artRefReq = useSafeRequest<any[]>(api, { maxAttempts: 3 });

    const loadRefs = async () => {

        try {
            setLoading(true);
            setError(null);

            const queryFilter = type ? `?type=${encodeURIComponent(type)}` : "";

            const tasks = [
                artRefReq.run({ method: "GET", url: `artworkreference/artwork/${artworkId}${queryFilter}` }),
            ];

            const [a] = await Promise.allSettled(tasks);

            if (a.status === "fulfilled") setRefs(a.value);

        } catch (e: any) {
            setError("Failed to load references");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        void loadRefs();
    }, [artworkId, type, refresh.reference]);

    const filteredRefs = useMemo(() => {
        if (!search.trim()) return refs;
        const q = search.toLowerCase();
        return refs.filter((r) => {
            return (
                r.details.toLowerCase().includes(q) ||
                (r.dateText && r.dateText.toLowerCase().includes(q)) ||
                (r.url && r.url.toLowerCase().includes(q))
            );
        });
    }, [refs, search]);

    const handleCreate = () => {
        setSelected({
            type,
            details: "",
            dateText: "",
            url: "",
        });
        setEditMode(true);
        setIsOpen(true);
    };

    const handleCancel = () => {
        if (!editMode) setIsOpen(false);
    }

    const handleSave = async (ref: ArtworkReference) => {

        try {
            const accessToken = await getAccessToken();
            if (!accessToken) return;

            const formData = new FormData();

            formData.append("ArtworkReferenceId", ref.artworkReferenceId?.toString() ?? "");
            formData.append("ArtworkId", artworkId.toString());
            formData.append("Type", ref.type);
            formData.append("Details", ref.details);

            if (ref.dateText) formData.append("DateText", ref.dateText);
            if (ref.url) formData.append("Url", ref.url);

            if (ref.artworkReferenceId) {
                await put(`artworkreference/${ref.artworkReferenceId}`, accessToken, formData);
            } else {
                await post(`artworkreference/${artworkId}`, accessToken, formData);
            }

            // reload
            await loadRefs();
            setSelected(null);
            setEditMode(false);
            setIsOpen(false);
        } catch (e: any) {
            alert("Failed to save reference");
        }
    };

    const handleDelete = async (ref: ArtworkReference) => {
        if (!ref.artworkReferenceId) return;
        const ok = window.confirm("Are you sure you want to delete this entry?");
        if (!ok) return;
        try {
            const accessToken = await getAccessToken();
            if (!accessToken) return;

            await del(`artworkreference/${ref.artworkReferenceId}`, accessToken);

            // Optimistic remove
            setRefs((prev) => prev.filter((r) => r.artworkReferenceId !== ref.artworkReferenceId));
            setSelected(null);
            setEditMode(false);
        } catch (e: any) {
            alert("Failed to delete reference");
        }
    };

    return (

        <div className="space-y-4">
            {/* Toolbar: Add + Search */}
            <div className="flex justify-between items-center gap-4">
                <button
                    className="responsive-button text-sm"
                    onClick={handleCreate}
                    hidden={true}
                >
                    Add
                </button>

                <input
                    type="text"
                    placeholder="Search details, date, URL…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="flex-1 border rounded px-2 py-1 text-sm"
                    title="Search"
                />
            </div>

            {/* Loading / Error */}
            {loading && (
                <div className="text-sm text-gray-600">Loading {type.toLowerCase()}…</div>
            )}
            {error && (
                <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">
                    {error}
                </div>
            )}

            <Divider sx={{ my: 2 }} />

            {/* List of cards */}
            {!loading && filteredRefs.length === 0 && !error && (
                <div className="text-sm text-gray-500">
                    No {type.toLowerCase() === "exhibit" ? "exhibition" : "literature"} entries yet.
                </div>
            )}

            <div className="reference-section">
                <div className="reference-cards">
                    {filteredRefs.map((r) => (

                        <div className="reference-card">
                            <button
                                key={r.artworkReferenceId}
                                type="button"
                                className="reference-tile"
                                onClick={() => {
                                    setSelected(r);
                                    setEditMode(false);
                                    setIsOpen(true);
                                }}
                            >
                                <div className={`reference-label ${type.toLowerCase()}`}> { r.dateText || "" }</div>
                                <div className={`reference-value ${type.toLowerCase()}`}>
                                {r.details}
                                </div>
                            </button>
                        </div>
                    ))}
                </div>
            </div>

            {/* Preview / Edit Panel */}
            {selected && (
                <ArtworkReferencePanel
                    reference={selected}
                    editMode={editMode}
                    isOpen={isOpen}
                    isViewOnly={isViewOnly}
                    setEditMode={setEditMode}
                    onClose={() => {
                        setSelected(null);
                        setEditMode(false);
                        setIsOpen(false);
                    }}
                    onSave={handleSave}
                    onDelete={handleDelete}
                    onCancel={handleCancel}
                />
            )}
                </div>

    );
};

export default ArtworkReferenceManager;
